import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Fade,
  IconButton,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon,
  VpnKey as VpnKeyIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack,
  Refresh as RefreshIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useTestAuthFlow } from '../hooks/useTestAuthFlow';
import '../styles/Auth.css';
import { PASSWORD_RULES, PASSWORD_MAX_LENGTH, validateNewPassword } from '../utils/passwordPolicy';
import AuthPageTemplate from '../components/auth/AuthPageTemplate';

const steps = ['Enter Email', 'Verify OTP', 'Reset Password'];

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const goToLogin = useCallback(() => {
    navigate('/login', { replace: true });
  }, [navigate]);
  const { testAuthFlowEnabled, testStaticCaptcha } = useTestAuthFlow();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [errors, setErrors] = useState<{
    email?: string;
    captcha?: string;
    otp?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  
  // Step 1: Email and Captcha
  const [email, setEmail] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const captchaRef = useRef<HTMLCanvasElement>(null);
  
  // Step 2: OTP Verification
  const [otp, setOtp] = useState('');
  
  // Step 3: New Password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });

  // Generate simple captcha
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 5; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    
    // Draw captcha on canvas
    if (captchaRef.current) {
      const canvas = captchaRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = 120;
        canvas.height = 40;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Background
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Text
        ctx.font = '20px Arial';
        ctx.fillStyle = '#333';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(result, canvas.width / 2, canvas.height / 2);
        
        // Add some noise
        for (let i = 0; i < 50; i++) {
          ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.3)`;
          ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
        }
      }
    }
  };

  // Initialize captcha on mount and when returning to step 1 (canvas must be in DOM)
  useEffect(() => {
    if (activeStep === 0) {
      generateCaptcha();
    }
  }, [activeStep]);

  // Auto-hide success messages
  useEffect(() => {
    if (message?.type === 'success') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const validateEmail = (): boolean => {
    const newErrors: typeof errors = {};
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    // Validate captcha: empty first, then wrong (per acceptance criteria)
    const captchaValue = (captchaInput || '').trim();
    if (captchaValue === '') {
      newErrors.captcha = 'Captcha is required';
    } else if (
      captchaValue !== captchaText
      && !(testAuthFlowEnabled && captchaValue === testStaticCaptcha)
    ) {
      newErrors.captcha = 'Invalid captcha';
      generateCaptcha();
      setCaptchaInput('');
    }
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Rely on field-level helperText errors for client-side validation;
      // avoid a generic banner message here.
      return false;
    }
    return true;
  };

  const handleStep1 = async () => {
    if (!validateEmail()) {
      return;
    }

    if (testAuthFlowEnabled && (captchaInput || '').trim() === testStaticCaptcha) {
      setMessage({
        text: 'A password reset OTP has been sent to your email. Please check your inbox.',
        type: 'success',
      });
      setActiveStep(1);
      setErrors({});
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await apiService.forgotPassword({ email: email.trim() });

      if (response.success) {
        setMessage({
          text: response.message || 'Password reset OTP has been sent to your email',
          type: 'success',
        });
        setActiveStep(1);
        setErrors({});
        generateCaptcha();
        setCaptchaInput('');
      } else {
        setMessage({
          text: response.message || 'Failed to send password reset email',
          type: 'error',
        });
        generateCaptcha(); // Generate new captcha on error
        setCaptchaInput('');
      }
    } catch (error: any) {
      setMessage({
        text: error.response?.data?.message || error.message || 'Failed to send password reset email',
        type: 'error',
      });
      generateCaptcha(); // Generate new captcha on error
      setCaptchaInput('');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    const newErrors: typeof errors = {};
    if (!otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (otp.length !== 6) {
      newErrors.otp = 'OTP must be 6 digits';
    } else if (/^(\d)\1{5}$/.test(otp.trim())) {
      newErrors.otp = 'OTP cannot contain all same digits';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Let the OTP field error explain the problem instead of a generic banner.
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await apiService.verifyOtp({
        email: email.trim(),
        otp: otp.trim(),
      });

      if (response.success) {
        setMessage({
          text: response.message || 'OTP verified successfully',
          type: 'success',
        });
        setActiveStep(2);
        setErrors({});
      } else {
        setMessage({
          text: response.message || 'Invalid or expired OTP',
          type: 'error',
        });
      }
    } catch (error: any) {
      setMessage({
        text: error.response?.data?.message || error.message || 'Failed to verify OTP',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStep3 = async () => {
    const newErrors: typeof errors = {};
    const newPwdError = validateNewPassword(newPassword);
    if (newPwdError) {
      newErrors.newPassword = newPwdError;
    }
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Let password field helperText and rules surface the validation issues.
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await apiService.resetPassword({
        email: email.trim(),
        otp: otp.trim(),
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      });

      if (response.success) {
        navigate('/login?passwordReset=1', { replace: true });
        return;
      } else {
        setMessage({
          text: response.message || 'Failed to reset password',
          type: 'error',
        });
      }
    } catch (error: any) {
      setMessage({
        text: error.response?.data?.message || error.message || 'Failed to reset password',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleStep1();
    } else if (activeStep === 1) {
      handleStep2();
    } else if (activeStep === 2) {
      handleStep3();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setMessage(null);
      setErrors({});
      if (activeStep === 1) {
        setCaptchaInput('');
        setOtp('');
      }
      if (activeStep === 2) {
        setNewPassword('');
        setConfirmPassword('');
      }
    }
  };

  return (
    <AuthPageTemplate
      variant="forgot-password"
      style={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        fontSize: '0.8rem',
        lineHeight: 1.35,
        zoom: 0.9,
      }}
      leftPanel={
        <div className="forgot-password-left-content">
          <VpnKeyIcon className="forgot-password-hero-icon" />
          <h2>Account recovery</h2>
          <p>Reset your password securely in three simple steps.</p>
          <ul>
            <li>Verify your email</li>
            <li>Enter the one-time code</li>
            <li>Choose a new strong password</li>
          </ul>
        </div>
      }
    >
      <Fade in timeout={600}>
        <Paper
          elevation={0}
          className="auth-card forgot-password-auth-card"
          sx={{
            p: { xs: 3, sm: 4, md: 4.5 },
            borderRadius: 0,
            maxWidth: 'none',
            width: '100%',
            overflow: 'hidden',
            boxShadow: 'none',
            background: '#ffffff',
          }}
        >
            {/* Header – aligned with Login (logo + title) */}
            <Box className="auth-logo-wrapper" sx={{ mb: 1 }}>
              <img
                src="/dragonflytest-logo.jpg"
                alt="DragonflyTest Logo"
                className="auth-logo"
              />
            </Box>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(102, 126, 234, 0.12)', mb: 1.5 }}>
                <VpnKeyIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5, color: '#333' }}>
                Forgot Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                Follow the steps below to reset your password
              </Typography>
            </Box>

            {/* Stepper – compact and aligned */}
            <Box sx={{ mb: 3.5, px: { xs: 0, sm: 1 } }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  '& .MuiStepLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
                  '& .MuiStepIcon-root.Mui-active': { color: '#667eea' },
                  '& .MuiStepIcon-root.Mui-completed': { color: '#667eea' },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

          {/* Message Alert */}
          {message && (
            <Alert
              severity={message.type}
              onClose={() => setMessage(null)}
              sx={{ mb: 2.5, borderRadius: 2 }}
            >
              {message.text}
            </Alert>
          )}

          {/* Step 1: Email and Captcha */}
          {activeStep === 0 && (
            <Fade in timeout={400}>
              <Box>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value.replace(/\s+/g, ''));
                    setErrors((prev) => ({ ...prev, email: undefined }));
                    if (message) setMessage(null);
                  }}
                  disabled={loading}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'action.active', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                />

                {/* Captcha Section – aligned row */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e8eaef',
                    bgcolor: '#f8f9fc',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#495057' }}>
                    Security verification
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Enter code"
                      value={captchaInput}
                      onChange={(e) => {
                        setCaptchaInput(e.target.value.replace(/\s+/g, ''));
                        setErrors((prev) => ({ ...prev, captcha: undefined }));
                        if (message) setMessage(null);
                      }}
                      disabled={loading}
                      error={!!errors.captcha}
                      helperText={errors.captcha || 'Case-sensitive'}
                      placeholder="e.g. Ab3xY"
                      size="small"
                      sx={{
                        flex: '1 1 140px',
                        minWidth: 140,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                      }}
                      inputProps={{
                        maxLength: 5,
                        style: {
                          letterSpacing: '0.15em',
                          fontFamily: 'monospace',
                          fontSize: '1rem',
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title="Refresh captcha" arrow>
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', cursor: 'pointer' }}
                          onClick={generateCaptcha}
                        >
                          <canvas
                            ref={captchaRef}
                            style={{
                              border: '2px solid #dee2e6',
                              borderRadius: 8,
                              cursor: 'pointer',
                              transition: 'border-color 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#667eea';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.25)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#dee2e6';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          />
                        </Box>
                      </Tooltip>
                      <Tooltip title="Refresh" arrow>
                        <IconButton
                          size="small"
                          onClick={generateCaptcha}
                          disabled={loading}
                          sx={{
                            color: '#667eea',
                            bgcolor: 'rgba(102, 126, 234, 0.08)',
                            '&:hover': { bgcolor: '#667eea', color: 'white' },
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Step 2: OTP Verification */}
          {activeStep === 1 && (
            <Fade in timeout={400}>
              <Box>
                <Box
                  sx={{
                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                    border: '1px solid rgba(102, 126, 234, 0.25)',
                    borderRadius: 2,
                    p: 2,
                    mb: 2.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: '#5568d3', fontWeight: 600, mb: 0.5 }}>
                    Verification code sent
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#5a67a8' }}>
                    We sent a 6-digit code to <strong>{email}</strong>
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                    setErrors((prev) => ({ ...prev, otp: undefined }));
                    if (message) setMessage(null);
                  }}
                  disabled={loading}
                  error={!!errors.otp}
                  helperText={errors.otp}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      letterSpacing: '0.5rem',
                      fontFamily: 'monospace',
                      fontWeight: 600,
                    },
                  }}
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Didn't receive it? Check spam or request again from step 1.
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Step 3: New Password */}
          {activeStep === 2 && (
            <Fade in timeout={400}>
              <Box>
                <TextField
                  fullWidth
                  label="New password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value.replace(/\s+/g, ''));
                    setErrors((prev) => ({ ...prev, newPassword: undefined }));
                    if (message) setMessage(null);
                  }}
                  disabled={loading}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                          edge="end"
                          size="small"
                        >
                          {showPasswords.new ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: errors.newPassword ? 'error.main' : '#e8eaef',
                    bgcolor: errors.newPassword ? 'rgba(211, 47, 47, 0.04)' : '#f8f9fc',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#495057' }}>
                    Password requirements
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: 'none' }}>
                    {PASSWORD_RULES.map((rule) => {
                      const met = rule.met(newPassword);
                      return (
                        <Box
                          key={rule.label}
                          component="li"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.25,
                            color: met ? 'success.dark' : 'text.secondary',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {met ? (
                            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
                          ) : (
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: 'action.disabled',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {rule.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  label="Confirm new password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value.replace(/\s+/g, ''));
                    setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                    if (message) setMessage(null);
                  }}
                  disabled={loading}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                          edge="end"
                          size="small"
                        >
                          {showPasswords.confirm ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
                  sx={{ mb: 0, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </Fade>
          )}

          {/* Action Buttons – aligned and responsive */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 3.5,
              pt: 2.5,
              borderTop: '1px solid #e8eaef',
              gap: 2,
            }}
          >
            <Button
              onClick={goToLogin}
              startIcon={<ArrowBack />}
              disabled={loading}
              sx={{
                color: '#5a67a8',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' },
              }}
            >
              Back to Login
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={loading}
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
                sx={{
                  minWidth: 120,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1.25,
                  boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    boxShadow: '0 6px 18px rgba(102, 126, 234, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': { background: '#b0b0b0', boxShadow: 'none' },
                  transition: 'all 0.2s ease',
                }}
              >
                {loading ? 'Processing...' : activeStep === 2 ? 'Reset password' : 'Next'}
              </Button>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5, pt: 2, textAlign: 'center', borderTop: '1px solid #e8eaef' }}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{' '}
              <Button
                type="button"
                onClick={goToLogin}
                disabled={loading}
                sx={{
                  p: 0,
                  minWidth: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#667eea',
                  verticalAlign: 'baseline',
                  '&:hover': { bgcolor: 'transparent', color: '#5568d3' },
                }}
              >
                Sign in
              </Button>
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </AuthPageTemplate>
  );
};

export default ForgotPassword;

